
import Vue, {
  computed,
  nextTick,
  onMounted,
  Ref,
  ref,
  toRef,
  PropType,
  watch,
} from 'vue';
import BCXSearchSelect from '@/components/molecules/BCXSearchSelect.vue';
import { convertToItemList } from '@/utils/selectItemTools';
import useUser from '@/mixins/useUser';
import { useStore } from 'vue2-helpers/vuex';
import { City } from '@/models/Geo';
import { SelectItem } from '@/models/SelectItem';

export default Vue.extend({
  components: {
    BCXSearchSelect
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    countryCode: {
      type: String,
      default: 'DE'
    },
    placeName: {
      type: String,
      default: undefined
    },
    postalCode: {
      type: String,
      default: undefined
    },
    initialCompanySize: {
      type: String,
      default: 'RANGE_50_500'
    },
    initialIndustry: {
      type: String,
      default: ''
    },
    countryList: {
      type: Array,
      default: () => []
    },
    cityList: {
      type: Array as PropType<City[]>,
      default: () => []
    },
    industryList: {
      type: Array,
      default: () => []
    },
    isCreatingProject: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    const { tUserFallback: tUser, isCustomer } = useUser('self');

    const country = ref('DE');
    const countryCode = toRef(props, 'countryCode');
    const countryList = toRef(props, 'countryList');
    const placeName = toRef(props, 'placeName');
    const countryItems = computed(() => convertToItemList(countryList.value, 'isoCode', 'countryName'));

    const cityList: Ref<City[]> = toRef(props, 'cityList');
    const postalCode = toRef(props, 'postalCode');

    const getDropdownItems = (translationKey: string): SelectItem[] => {
      const translation = {
        ...(tUser(
          `project-forum_USERTYPE.form-section.client.${translationKey}.dropdown`
        ) as any)
      };
      const keys = Object.keys(translation);
      return keys.map((key) => ({
        text: translation[key],
        value: key
      }));
    };

    const selectedCity = ref<Partial<City> | null>(null);

    /**
     * Formats a City object into a string for display in the dropdown.
     *
     * @param city - The city to format (only placeName and postalCode are used)
     * @returns A formatted string combining postal code and place name in the format "postalCode, placeName".
     */
    const formatCityLocation = ({ placeName, postalCode }: Partial<City>) => {
      if (postalCode && placeName) {
        return `${postalCode}, ${placeName}`;
      }

      return '';
    };

    const cityItems = computed<SelectItem[]>(() => cityList.value.map((city) => {
      const text = formatCityLocation(city);
      return ({
        text,
        value: text
      });
    }));

    /**
     * Adapter between domain state (selectedCity) and the search-select UI.
     * The UI works with a formatted string, while the app state must remain a City object.
     * Using a computed getter/setter gives us a single source of truth (selectedCity)
     * and avoids having to manually keep a string value and a City object in sync.
     */
    const selectedCityValue = computed<string>({
      get() {
        return selectedCity.value
          ? formatCityLocation(selectedCity.value)
          : '';
      },
      set(value: string) {
        const city = cityList.value.find(
          (c) => formatCityLocation(c) === value
        ) ?? null;

        selectedCity.value = city;
      }
    });

    const companySizeItems = computed(() => getDropdownItems('company-size'));
    const companySize = ref('RANGE_50_500');
    const initialCompanySize = toRef(props, 'initialCompanySize');
    const showCompanySize = computed(() => true);

    const industry = ref('');
    const industryList: any = toRef(props, 'industryList');
    const industryItems = computed(() => convertToItemList(industryList.value, 'externalId', 'label'));
    const showIndustry = computed(() => true);

    const pushValue = () => {
      const clientData = {
        countryCode: country.value,
        cityPlaceName: selectedCity.value?.placeName ?? '',
        cityPostalCode: selectedCity.value?.postalCode ?? '',
        companySize: companySize.value,
        industry: industry.value
      };
      emit('input', clientData);
    };

    watch(() => selectedCity.value, () => {
      pushValue();
    });

    watch(() => country.value, (value) => {
      if (value !== selectedCity.value?.countryCode) {
        selectedCity.value = null;
      }
      pushValue();
    });

    watch(() => companySize.value, () => {
      pushValue();
    });

    watch(() => props.value.companySize, (value) => {
      if (value && value !== companySize.value) {
        companySize.value = value;
      }
    });

    watch(() => industry.value, (value) => {
      if (value !== props.value.industry) {
        nextTick(() => {
          pushValue();
        });
      }
    });

    const fetchCities = (countryId: string, searchString: string) => {
      store.dispatch('projectForum/fetchCities', { countryId, searchString });
    };

    const inputCity = (value: string) => {
      fetchCities(country.value, value);
    };

    onMounted(() => {
      if (countryCode.value) {
        country.value = countryCode.value;
      }

      if (placeName.value && postalCode.value) {
        selectedCity.value = {
          placeName: placeName.value,
          postalCode: postalCode.value,
          countryCode: countryCode.value
        };
      }

      companySize.value = initialCompanySize.value;

      industry.value = props.initialIndustry || '';
    });

    return {
      country,
      cityItems,
      companySize,
      companySizeItems,
      industry,
      industryItems,
      selectedCityValue,
      countryItems,
      fetchCities,
      tUser,
      getDropdownItems,
      inputCity,
      formatCityLocation,
      isCustomer,
      showCompanySize,
      showIndustry
    };
  }
});
